import { useState, useEffect, useCallback } from 'react';
import { getSankeyByProyecto, getSankeyByElemento, getSankeyByTarjeta, transformSankeyData } from '../services/sankeyService';

/**
 * Hook personalizado para manejar gráficas Sankey
 * @param {string} type - Tipo de gráfica: 'proyecto', 'elemento', 'tarjeta'
 * @param {string} id - ID del recurso
 * @param {Object} params - Parámetros opcionales para la consulta
 * @param {string} params.fechaInicio - Fecha de inicio (ISO 8601)
 * @param {string} params.fechaFin - Fecha de fin (ISO 8601)
 * @param {string[]} params.unidades - Filtrar por unidades específicas
 * @returns {Object} Estado y funciones para manejar la gráfica Sankey
 */
export const useSankey = (type, id, params = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [transformedData, setTransformedData] = useState(null);

  const fetchSankeyData = useCallback(async () => {
    if (!id || !type) return;

    setLoading(true);
    setError(null);

    try {
      let response;
      
      // Preparar parámetros limpios (sin valores undefined)
      const cleanParams = {};
      if (params.fechaInicio) cleanParams.fechaInicio = params.fechaInicio;
      if (params.fechaFin) cleanParams.fechaFin = params.fechaFin;
      if (params.unidades && params.unidades.length > 0) {
        cleanParams.unidades = params.unidades.filter(u => u && u.trim().length > 0);
      }
      
      switch (type) {
        case 'proyecto':
          response = await getSankeyByProyecto(id, cleanParams);
          break;
        case 'elemento':
          response = await getSankeyByElemento(id, cleanParams);
          break;
        case 'tarjeta':
          response = await getSankeyByTarjeta(id, cleanParams);
          break;
        default:
          throw new Error(`Tipo de gráfica Sankey no válido: ${type}`);
      }

      setData(response);
      
      // Transformar datos para el componente
      const transformed = transformSankeyData(response);
      setTransformedData(transformed);
      
    } catch (err) {
      console.error(`Error al obtener gráfica Sankey para ${type}:`, err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [type, id, JSON.stringify(params)]);

  useEffect(() => {
    fetchSankeyData();
  }, [fetchSankeyData]);

  const refetch = useCallback(() => {
    fetchSankeyData();
  }, [fetchSankeyData]);

  return {
    data,
    transformedData,
    loading,
    error,
    refetch,
    hasData: transformedData && transformedData.chartData && transformedData.chartData.length > 1
  };
};

/**
 * Hook para manejar múltiples gráficas Sankey
 * @param {Array} configs - Array de configuraciones {type, id, params}
 * @returns {Object} Estado consolidado de múltiples gráficas
 */
export const useMultipleSankey = (configs = []) => {
  const [results, setResults] = useState({});
  const [globalLoading, setGlobalLoading] = useState(false);
  const [globalError, setGlobalError] = useState(null);

  const fetchMultipleData = useCallback(async () => {
    if (configs.length === 0) return;

    setGlobalLoading(true);
    setGlobalError(null);
    const newResults = {};

    try {
      const promises = configs.map(async (config, index) => {
        const { type, id, params = {} } = config;
        const key = config.key || `${type}_${id}_${index}`;

        try {
          let response;
          
          // Preparar parámetros limpios
          const cleanParams = {};
          if (params.fechaInicio) cleanParams.fechaInicio = params.fechaInicio;
          if (params.fechaFin) cleanParams.fechaFin = params.fechaFin;
          if (params.unidades && params.unidades.length > 0) {
            cleanParams.unidades = params.unidades.filter(u => u && u.trim().length > 0);
          }
          
          switch (type) {
            case 'proyecto':
              response = await getSankeyByProyecto(id, cleanParams);
              break;
            case 'elemento':
              response = await getSankeyByElemento(id, cleanParams);
              break;
            case 'tarjeta':
              response = await getSankeyByTarjeta(id, cleanParams);
              break;
            default:
              throw new Error(`Tipo de gráfica Sankey no válido: ${type}`);
          }

          const transformed = transformSankeyData(response);
          
          newResults[key] = {
            data: response,
            transformedData: transformed,
            loading: false,
            error: null,
            hasData: transformed && transformed.chartData && transformed.chartData.chartData.length > 1
          };
        } catch (err) {
          newResults[key] = {
            data: null,
            transformedData: null,
            loading: false,
            error: err,
            hasData: false
          };
        }
      });

      await Promise.all(promises);
      setResults(newResults);
      
    } catch (err) {
      console.error('Error al obtener múltiples gráficas Sankey:', err);
      setGlobalError(err);
    } finally {
      setGlobalLoading(false);
    }
  }, [JSON.stringify(configs)]);

  useEffect(() => {
    fetchMultipleData();
  }, [fetchMultipleData]);

  const refetchAll = useCallback(() => {
    fetchMultipleData();
  }, [fetchMultipleData]);

  return {
    results,
    globalLoading,
    globalError,
    refetchAll,
    hasAnyData: Object.values(results).some(result => result.hasData)
  };
};